import type { Html, HtmlBuilder } from "foldkit/html";
import * as lucide from "lucide";

import { i18next } from "../i18n";
import { icon } from "./icons";
import { agentLabelText } from "./provenance";

/**
 * Shared visual primitives for the A2A pages (Tasks, Knowledge, Agents).
 * Every chip is an icon plus visible text, so no state is told by color alone;
 * each carries `role="img"` and a full accessible name ("Status: In progress").
 *
 * Time is pure here: callers pass `now` (epoch ms). Pages read
 * `Clock.currentTimeMillis` in their fetch command and keep it beside the
 * result, so views and tests never touch the wall clock.
 */

/** Full literal keys (the i18n checker's scope rule does not match `a2aUi`). */
const t = (key: `a2aUi.${string}`, options: Record<string, unknown> = {}) =>
	i18next.t(key, options);

type IconNode = typeof lucide.Clock;

const glyph = <M>(
	h: HtmlBuilder<M>,
	name: keyof typeof lucide,
	className = "",
	size = 14,
) =>
	icon(h, name, lucide[name] as IconNode, {
		size,
		class: `shrink-0 ${className}`,
	});

/**
 * Hue lives in the soft tint and the icon; text is always base-content, so it
 * keeps 4.5:1 in every theme (a soft badge's own coloured text drops to
 * 1.5:1 for warning on corporate). Class names are literal for Tailwind.
 */
type Tone =
	| "info"
	| "success"
	| "warning"
	| "error"
	| "secondary"
	| "ghost"
	| "plain";

const TONE: Record<Tone, { chip: string; icon: string }> = {
	info: { chip: "badge-info badge-soft text-base-content", icon: "text-info" },
	success: {
		chip: "badge-success badge-soft text-base-content",
		icon: "text-success",
	},
	warning: {
		chip: "badge-warning badge-soft text-base-content",
		icon: "text-warning",
	},
	error: {
		chip: "badge-error badge-soft text-base-content",
		icon: "text-error",
	},
	secondary: {
		chip: "badge-secondary badge-soft text-base-content",
		icon: "text-secondary",
	},
	ghost: { chip: "badge-ghost", icon: "" },
	// Uncoloured soft (base-content on a base-content tint), not badge-neutral: neutral-soft is dark on near-black in the dark theme.
	plain: { chip: "badge-soft text-base-content", icon: "" },
};

type Style = { iconName: keyof typeof lucide; tone: Tone };

const chip = <M>(
	h: HtmlBuilder<M>,
	{
		testId,
		label,
		iconName,
		text,
		tone,
	}: Style & { testId: string; label: string; text: string },
): Html =>
	h.span(
		[
			h.Role("img"),
			h.AriaLabel(label),
			h.DataAttribute("testid", testId),
			h.Class(
				`badge badge-sm gap-1 whitespace-nowrap max-w-full ${TONE[tone].chip}`,
			),
		],
		[
			glyph(h, iconName, TONE[tone].icon),
			h.span([h.Class("min-w-0 truncate")], [text]),
		],
	);

// ---- Status ---------------------------------------------------------------

export const STATUSES = [
	"backlog",
	"todo",
	"in_progress",
	"done",
	"canceled",
] as const;
export type Status = (typeof STATUSES)[number];

const STATUS_STYLE: Record<Status, Style> = {
	backlog: { iconName: "CircleDashed", tone: "ghost" },
	todo: { iconName: "Circle", tone: "ghost" },
	in_progress: { iconName: "CircleDot", tone: "info" },
	done: { iconName: "CircleCheck", tone: "success" },
	canceled: { iconName: "CircleSlash", tone: "plain" },
};

export const statusLabel = (status: Status): string =>
	t(`a2aUi.status.${status}`);

export const statusChip = <M>(h: HtmlBuilder<M>, status: Status): Html =>
	chip(h, {
		testId: `status-${status}`,
		label: t("a2aUi.statusLabel", { status: statusLabel(status) }),
		text: statusLabel(status),
		...STATUS_STYLE[status],
	});

// ---- Priority -------------------------------------------------------------

export const PRIORITIES = ["urgent", "high", "medium", "low", "none"] as const;
export type Priority = (typeof PRIORITIES)[number];

const PRIORITY_STYLE: Record<
	Priority,
	{ iconName: keyof typeof lucide; className: string }
> = {
	urgent: { iconName: "CircleAlert", className: "text-error" },
	high: { iconName: "SignalHigh", className: "" },
	medium: { iconName: "SignalMedium", className: "" },
	low: { iconName: "SignalLow", className: "" },
	none: { iconName: "Minus", className: "opacity-60" },
};

export const priorityLabel = (priority: Priority): string =>
	t(`a2aUi.priority.${priority}`);

/** Icon plus visible label; the colour (urgent only) is on the icon, the text stays base-content. */
export const priorityGlyph = <M>(
	h: HtmlBuilder<M>,
	priority: Priority,
): Html => {
	const label = t("a2aUi.priorityLabel", { priority: priorityLabel(priority) });
	return h.span(
		[
			h.Role("img"),
			h.AriaLabel(label),
			h.Title(label),
			h.DataAttribute("testid", `priority-${priority}`),
			h.Class("inline-flex items-center gap-1 text-xs text-base-content"),
		],
		[
			glyph(
				h,
				PRIORITY_STYLE[priority].iconName,
				PRIORITY_STYLE[priority].className,
			),
			h.span([], [priorityLabel(priority)]),
		],
	);
};

// ---- Grade ----------------------------------------------------------------

export type GradeBand = "strong" | "fair" | "weak" | "ungraded";

/** Grades are 0–10: 8 and up is strong, 6 and up fair, below that weak. */
export const gradeBand = (grade: number | null): GradeBand =>
	grade === null
		? "ungraded"
		: grade >= 8
			? "strong"
			: grade >= 6
				? "fair"
				: "weak";

const GRADE_STYLE: Record<GradeBand, Style> = {
	strong: { iconName: "CircleCheck", tone: "success" },
	fair: { iconName: "CircleDot", tone: "warning" },
	weak: { iconName: "CircleAlert", tone: "error" },
	ungraded: { iconName: "CircleDashed", tone: "ghost" },
};

/**
 * The number is always shown; the band only adds an icon and a tint. The band
 * comes from the shown (rounded) number, so 7.96 reads "8.0" and is strong.
 */
export const gradeChip = <M>(h: HtmlBuilder<M>, grade: number | null): Html => {
	const shown = grade === null ? null : Math.round(grade * 10) / 10;
	const band = gradeBand(shown);
	const text = shown === null ? "—" : shown.toFixed(1);
	return chip(h, {
		testId: `grade-${band}`,
		label:
			grade === null
				? t("a2aUi.grade.ungraded")
				: t("a2aUi.gradeLabel", {
						grade: text,
						band: t(`a2aUi.grade.${band}`),
					}),
		text,
		...GRADE_STYLE[band],
	});
};

// ---- Wiki link state ------------------------------------------------------

export type LinkState = "resolved" | "ambiguous" | "broken";

const LINK_STYLE: Record<LinkState, Style> = {
	resolved: { iconName: "Link", tone: "ghost" },
	ambiguous: { iconName: "Split", tone: "warning" },
	broken: { iconName: "Unlink", tone: "error" },
};

export const linkStateChip = <M>(h: HtmlBuilder<M>, state: LinkState): Html =>
	chip(h, {
		testId: `link-${state}`,
		label: t(`a2aUi.link.${state}Label`),
		text: t(`a2aUi.link.${state}`),
		...LINK_STYLE[state],
	});

// ---- Retention / expiry ---------------------------------------------------

/** "45 min", "2 hr", "3 d": minutes round up, hours to the nearest, days down. */
export const durationText = (ms: number): string => {
	const minutes = Math.max(1, Math.ceil(ms / 60_000));
	if (minutes < 60) return t("a2aUi.duration.minutes", { count: minutes });
	const hours = Math.round(ms / 3_600_000);
	return hours < 48
		? t("a2aUi.duration.hours", { count: hours })
		: t("a2aUi.duration.days", { count: Math.floor(ms / 86_400_000) });
};

/** `expiresAt` null means permanent retention. */
export const retentionChip = <M>(
	h: HtmlBuilder<M>,
	expiresAt: string | null,
	now: number,
): Html => {
	if (expiresAt === null)
		return chip(h, {
			testId: "retention-permanent",
			label: t("a2aUi.retention.permanentLabel"),
			text: t("a2aUi.retention.permanent"),
			iconName: "Infinity",
			tone: "ghost",
		});
	const left = Date.parse(expiresAt) - now;
	if (left <= 0)
		return chip(h, {
			testId: "retention-expired",
			label: t("a2aUi.retention.expired"),
			text: t("a2aUi.retention.expired"),
			iconName: "TimerOff",
			tone: "plain",
		});
	const text = t("a2aUi.retention.expiresIn", { duration: durationText(left) });
	return chip(h, {
		testId: "retention-temporary",
		label: text,
		text,
		iconName: "Hourglass",
		tone: left < 3_600_000 ? "warning" : "info",
	});
};

// ---- Agent badge ----------------------------------------------------------

/** An agent (api key) by its name, or the generic provenance "Agent" label. */
export const agentBadge = <M>(h: HtmlBuilder<M>, name: string | null): Html => {
	const text = name ?? agentLabelText(null);
	return h.span(
		// Long names truncate (the full name stays in the accessible name and title).
		[h.Class("inline-flex max-w-48 min-w-0"), h.Title(text)],
		[
			chip(h, {
				testId: "agent-badge",
				label: name === null ? text : t("a2aUi.agentLabel", { name }),
				text,
				iconName: "Bot",
				tone: "secondary", // provenance's mcp colour (badge-secondary)
			}),
		],
	);
};

// ---- Relative time --------------------------------------------------------

const dayKey = (ms: number, timeZone?: string): number => {
	const [y, m, d] = new Intl.DateTimeFormat("en-CA", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	})
		.format(ms)
		.split("-")
		.map(Number);
	return Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1) / 86_400_000;
};

/**
 * "Just now" (incl. up to 1 min of skew ahead), "12 min ago", "2 hr ago" (same day), "Yesterday 14:32",
 * "4 days ago" (under a week), then a date ("Sep 30", or "Sep 30, 2025" in
 * another year; also any time over a minute ahead). Unparseable input is
 * returned as is. Pure: `now` comes from Clock at the command boundary.
 */
export const relativeTime = (
	iso: string,
	now: number,
	timeZone?: string,
): string => {
	const then = Date.parse(iso);
	// Unparseable: show the raw value rather than let Intl throw on NaN.
	if (Number.isNaN(then)) return iso;
	const elapsed = now - then;
	const language = i18next.language;
	const date = () => {
		const year = new Intl.DateTimeFormat("en", { timeZone, year: "numeric" });
		const sameYear = year.format(now) === year.format(then);
		return new Intl.DateTimeFormat(language, {
			timeZone,
			month: "short",
			day: "numeric",
			...(sameYear ? {} : { year: "numeric" }),
		}).format(then);
	};
	// Up to a minute ahead is clock skew; further ahead is a real future date.
	if (elapsed < -60_000) return date();
	if (elapsed < 60_000) return t("a2aUi.time.justNow");
	if (elapsed < 3_600_000)
		return t("a2aUi.time.minutesAgo", { count: Math.floor(elapsed / 60_000) });
	const days = dayKey(now, timeZone) - dayKey(then, timeZone);
	if (days === 0)
		return t("a2aUi.time.hoursAgo", { count: Math.floor(elapsed / 3_600_000) });
	if (days === 1)
		return t("a2aUi.time.yesterdayAt", {
			time: new Intl.DateTimeFormat(language, {
				timeZone,
				hour: "2-digit",
				minute: "2-digit",
				hourCycle: "h23",
			}).format(then),
		});
	return days < 7 ? t("a2aUi.time.daysAgo", { count: days }) : date();
};
